import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FB_APP_ID } from '../helpers/constants';

interface FacebookSdk {
  init(options: { appId: string; cookie: boolean; xfbml: boolean; version: string }): void;
  ui(options: { display: string; method: string; href: string; description: string },
    callback: (response?: { error_message?: string } | null) => void): void;
}
type FacebookWindow = Window & { FB?: FacebookSdk; fbAsyncInit?: () => void };

@Injectable({ providedIn: 'root' })
export class FacebookService {
  private readonly platformId = inject(PLATFORM_ID);
  private sdkLoad: Promise<void> | null = null;

  loadFacebookSDK(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();
    if (this.sdkLoad) return this.sdkLoad;

    this.sdkLoad = new Promise<void>((resolve, reject) => {
      const facebookWindow = window as FacebookWindow;
      const previousInit = facebookWindow.fbAsyncInit;
      const existing = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
      const script = existing ?? document.createElement('script');
      let settled = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        script.removeEventListener('error', onError);
        if (facebookWindow.fbAsyncInit === initialize) facebookWindow.fbAsyncInit = previousInit;
        if (error) {
          if (!existing) script.remove();
          reject(error);
        } else resolve();
      };
      const initialize = () => {
        if (settled) return;
        try {
          if (!facebookWindow.FB) throw new Error('Facebook SDK unavailable');
          facebookWindow.FB.init({ appId: FB_APP_ID, cookie: true, xfbml: true, version: 'v20.0' });
          finish();
        } catch {
          finish(new Error('Facebook SDK initialization failed'));
        }
      };
      const onError = () => finish(new Error('Facebook SDK failed to load'));
      const timer = setTimeout(() => finish(new Error('Facebook SDK load timed out')), 8000);
      facebookWindow.fbAsyncInit = initialize;
      script.addEventListener('error', onError);
      if (facebookWindow.FB) initialize();
      else if (!existing) {
        script.id = 'facebook-jssdk';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        document.head.appendChild(script);
      }
    }).catch(error => {
      this.sdkLoad = null;
      throw error;
    });
    return this.sdkLoad;
  }

  async shareUrlOrImage(url: string, description: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      await this.loadFacebookSDK();
      (window as FacebookWindow).FB!.ui({ display: 'popup', method: 'share', href: url, description }, response => {
        if (response?.error_message) console.error('Error sharing to Facebook:', response.error_message);
      });
    } catch {
      const query = new URLSearchParams({ u: url, quote: description });
      window.open(`https://www.facebook.com/sharer/sharer.php?${query}`, '_blank', 'noopener,noreferrer');
    }
  }
}
